<template>
  <div class="py-4 px-2 p-md-4">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4 class="mb-0">License Management</h4>
    </div>

    <a-row :gutter="16" class="mb-4">
      <a-col :xs="24" :sm="12" :md="8">
        <a-select
          v-model:value="selectedOrg"
          placeholder="Select Organization"
          style="width: 100%"
          show-search
          :loading="isLoadingOrgs"
          :options="orgOptions"
          option-filter-prop="label"
          @change="handleOrgChange"
        />
      </a-col>
      <a-col :xs="24" :sm="12" :md="4" class="mt-2 mt-sm-0">
        <a-button
          :loading="isLoading"
          :disabled="!selectedOrg"
          @click="refreshData"
        >
          <template #icon><i class="bi bi-arrow-clockwise"></i></template>
          Refresh
        </a-button>
      </a-col>
    </a-row>

    <template v-if="selectedOrg">
      <license-pool-card
        :licenses="adminOrgLicenses"
        :loading="isFetchingLicenses"
        class="mb-4"
        @edit="showUpdateModal = true"
      />

      <a-tabs v-model:activeKey="activeTab">
        <a-tab-pane key="devices" tab="Devices">
          <admin-device-table
            :devices="filteredAdminOrgDevices"
            :loading="isFetchingDevices"
            :filter="adminDeviceFilter"
            :available-licenses="adminOrgLicenses.available_count"
            @filter-change="handleDeviceFilterChange"
            @assign="handleAssign"
            @revoke="handleRevoke"
          />
        </a-tab-pane>
        <a-tab-pane key="events" tab="Event History">
          <event-history-panel
            :events="adminLicenseEvents"
            :loading="isFetchingEvents"
            @filter-change="handleEventFilterChange"
          />
        </a-tab-pane>
      </a-tabs>
    </template>

    <a-empty
      v-else
      description="Select an organization to manage licenses"
      class="mt-5"
    />

    <update-license-modal
      v-model:visible="showUpdateModal"
      :current-total="adminOrgLicenses.total_licenses"
      :assigned="adminOrgLicenses.assigned_count"
      :loading="isUpdatingLicenses"
      @submit="handleUpdateLicenses"
    />

    <assign-license-modal
      v-model:visible="showAssignModal"
      :device="selectedDevice"
      :loading="isAssigning"
      @confirm="confirmAssign"
    />

    <revoke-license-modal
      v-model:visible="showRevokeModal"
      :device="selectedDevice"
      :loading="isRevoking"
      @confirm="confirmRevoke"
    />
  </div>
</template>

<script>
import { inject } from 'vue';
import { mapGetters, mapActions } from 'vuex';
import LicensePoolCard from './LicensePoolCard.vue';
import UpdateLicenseModal from './UpdateLicenseModal.vue';
import AdminDeviceTable from './AdminDeviceTable.vue';
import EventHistoryPanel from './EventHistoryPanel.vue';
import AssignLicenseModal from './AssignLicenseModal.vue';
import RevokeLicenseModal from './RevokeLicenseModal.vue';

export default {
  name: 'AdminLicenseManagement',
  components: {
    LicensePoolCard,
    UpdateLicenseModal,
    AdminDeviceTable,
    EventHistoryPanel,
    AssignLicenseModal,
    RevokeLicenseModal,
  },

  setup() {
    const toast = inject('toast');
    return { toast };
  },

  data() {
    return {
      selectedOrg: null,
      activeTab: 'devices',
      showUpdateModal: false,
      showAssignModal: false,
      showRevokeModal: false,
      selectedDevice: null,
      isLoadingOrgs: false,
      isFetchingLicenses: false,
      isFetchingDevices: false,
      isFetchingEvents: false,
      isUpdatingLicenses: false,
      isAssigning: false,
      isRevoking: false,
    };
  },

  computed: {
    ...mapGetters([
      'organizations',
      'adminOrgLicenses',
      'filteredAdminOrgDevices',
      'adminDeviceFilter',
      'adminLicenseEvents',
    ]),
    orgOptions() {
      return this.organizations?.map((org) => ({
        value: org.Org_name,
        label: org.org_name_to_display || org.Org_name,
      }));
    },
    isLoading() {
      return this.isFetchingLicenses || this.isFetchingDevices;
    },
  },

  async created() {
    await this.loadOrganizations();
  },

  methods: {
    ...mapActions([
      'getOrganizations',
      'setAdminSelectedOrg',
      'fetchAdminOrgLicenses',
      'updateAdminOrgLicenses',
      'fetchAdminOrgDevices',
      'setAdminDeviceFilter',
      'adminAssignLicenseToDevice',
      'adminRevokeLicenseFromDevice',
      'fetchAdminLicenseEvents',
    ]),

    async loadOrganizations() {
      this.isLoadingOrgs = true;
      await this.getOrganizations();
      this.isLoadingOrgs = false;
    },

    async handleOrgChange(orgName) {
      this.selectedOrg = orgName;
      this.setAdminSelectedOrg(orgName);
      await this.loadOrgData();
    },

    async loadOrgData() {
      await Promise.all([
        this.loadLicenses(),
        this.loadDevices(),
        this.loadEvents(),
      ]);
    },

    async loadLicenses() {
      this.isFetchingLicenses = true;
      const [error] = await this.fetchAdminOrgLicenses();
      this.isFetchingLicenses = false;
      if (error) {
        this.handleError(error, 'Failed to load license information');
      }
    },

    async loadDevices() {
      this.isFetchingDevices = true;
      const [error] = await this.fetchAdminOrgDevices();
      this.isFetchingDevices = false;
      if (error) {
        this.handleError(error, 'Failed to load devices');
      }
    },

    async loadEvents(filters = {}) {
      this.isFetchingEvents = true;
      const [error] = await this.fetchAdminLicenseEvents(filters);
      this.isFetchingEvents = false;
      if (error) {
        this.handleError(error, 'Failed to load event history');
      }
    },

    refreshData() {
      if (this.selectedOrg) {
        this.loadOrgData();
      }
    },

    handleDeviceFilterChange(filter) {
      this.setAdminDeviceFilter(filter);
    },

    handleEventFilterChange(filters) {
      this.loadEvents(filters);
    },

    handleAssign(device) {
      this.selectedDevice = device;
      this.showAssignModal = true;
    },

    handleRevoke(device) {
      this.selectedDevice = device;
      this.showRevokeModal = true;
    },

    async confirmAssign(device) {
      this.isAssigning = true;
      const [error, data] = await this.adminAssignLicenseToDevice(device.id);
      this.isAssigning = false;

      if (error) {
        this.handleError(error, 'Failed to assign license');
        return;
      }

      this.toast.success(data?.message || 'License assigned successfully');
      this.showAssignModal = false;
      this.selectedDevice = null;
    },

    async confirmRevoke(device) {
      this.isRevoking = true;
      const [error, data] = await this.adminRevokeLicenseFromDevice(device.id);
      this.isRevoking = false;

      if (error) {
        this.handleError(error, 'Failed to revoke license');
        return;
      }

      this.toast.success(data?.message || 'License revoked successfully');
      this.showRevokeModal = false;
      this.selectedDevice = null;
    },

    async handleUpdateLicenses(totalLicenses) {
      this.isUpdatingLicenses = true;
      const [error] = await this.updateAdminOrgLicenses(totalLicenses);
      this.isUpdatingLicenses = false;

      if (error) {
        this.handleError(error, 'Failed to update license count');
        return;
      }

      this.toast.success('License count updated successfully');
      this.showUpdateModal = false;
    },

    handleError(error, defaultMessage) {
      const errorCode = error?.response?.data?.error;
      const errorMessage = this.getErrorMessage(
        errorCode,
        error,
        defaultMessage
      );
      this.toast.error(errorMessage);
    },

    getErrorMessage(errorCode, error, defaultMessage) {
      const errorMessages = {
        CANNOT_REDUCE_LICENSES:
          'Cannot reduce total licenses below the number of assigned licenses. Please revoke some licenses first.',
        ORGANIZATION_NOT_FOUND: 'Organization not found.',
        NO_LICENSES_AVAILABLE:
          'No licenses available for this organization.',
        ALREADY_ASSIGNED: 'This device already has a license assigned.',
        NO_ACTIVE_LICENSE:
          "This device doesn't have an active license to revoke.",
        DEVICE_NOT_FOUND: 'Device not found. It may have been removed.',
        DEVICE_EXEMPT:
          'This device is exempt from licensing (visible to support only).',
      };

      if (errorCode && errorMessages[errorCode]) {
        return errorMessages[errorCode];
      }

      return (
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        defaultMessage
      );
    },
  },
};
</script>

<style scoped>
h4 {
  color: #213547;
}
</style>
